import { useMutation, useQuery } from "@tanstack/react-query";
import useAxiosInstance from "./axiosInstance.utils";
import { errorFormat } from "./handleError.utils";
import { onSuccessMessage, queryClient } from "./reactQuery.utils";
import { useCallback, useEffect, useState } from "react";
import { objectToQueryString } from "./object.utils";
import { printDate } from "./date.utils";

/**
 * :::::::MUTATIONS:::::::::::
 */

export function useCreateCustomerAttachmentMutation(keyData) {
  const { postApi } = useAxiosInstance();
  async function createCustomerAttachmentApi(data) {
    try {
      const api = `/customer/attachment`;
      const apiResponse = await postApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["createCustomerAttachmentApi", ...keyData],
    mutationFn: (data) => createCustomerAttachmentApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("customerAttachments");
      onSuccessMessage(data, 201);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, createCustomerAttachment: mutateAsync };
}

export function useUpdateCustomerAttachmentMutation(keyData) {
  const { putApi } = useAxiosInstance();
  async function updateCustomerAttachmentApi(data) {
    try {
      const api = `/customer/attachment`;
      const apiResponse = await putApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }
  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["updateCustomerAttachmentApi", ...keyData],
    mutationFn: (data) => updateCustomerAttachmentApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("customerAttachments");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, updateCustomerAttachment: mutateAsync };
}

export function useDeleteCustomerAttachmentMutation(keyData) {
  const { deleteApi } = useAxiosInstance();
  async function deleteCustomerAttachmentApi(id) {
    try {
      const api = `/customer/attachment/${id}`;
      const apiResponse = await deleteApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["deleteCustomerAttachmentApi", ...keyData],
    mutationFn: (data) => deleteCustomerAttachmentApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("customerAttachments");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, deleteCustomerAttachment: mutateAsync };
}

/**
 * :::::::QUERIES:::::::::::
 */
export function useGetAllCustomerAttachmentQuery({ query, options = {}, key = "" }) {
  const { getApi } = useAxiosInstance();
  async function getAllCustomerAttachmentApi(query) {
    try {
      const api = `/customer/attachments${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const {
    data,
    isPending: loading,
    ...rest
  } = useQuery({
    queryKey: [`getAllCustomerAttachment-${query ?? ""}${key ? "-" + key : ""}`],
    queryFn: () => getAllCustomerAttachmentApi(query),
    ...options
  });
  const newData = data?.data?.data ?? {};
  const parents = data?.data?.parents ?? [];
  return { data: newData, parents, loading, ...rest };
}

export function usePrintCustomerAttachmentQuery({ query, options = {}, key = "" }) {
  const { getApi } = useAxiosInstance();

  async function printCustomerAttachmentApi(query) {
    try {
      const api = `/customer/pdf${query ?? ""}`;
      const apiResponse = await getApi(api);

      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const queryKey = [`printCustomerAttachment-${query ?? ""}${key ? "-" + key : ""}`];

  const {
    data,
    isPending: loading,
    ...rest
  } = useQuery({
    queryKey,
    queryFn: () => printCustomerAttachmentApi(query),
    ...options
  });
  const newData = data?.data ?? {};

  return { data: newData, loading, ...rest };
}

// get customer attachment query
export function useGetCustomerAttachmentQuery({ query, options = {}, key = "" }) {
  const { getApi } = useAxiosInstance();
  async function getCustomerAttachmentApi(query) {
    try {
      const api = `/customer/attachment${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { data, isPending: loading } = useQuery({
    queryKey: [`getCustomerAttachment-${query ?? ""}${key ? "-" + key : ""}`],
    queryFn: () => getCustomerAttachmentApi(query),
    ...options
  });
  const newData = data?.data?.data ?? {};
  return { data: newData, loading };
}

// functions
const customerAttachmentTypes = [
  {
    id: 1,
    name: "Quotation"
  },
  {
    id: 2,
    name: "Money Receipt"
  }
];

// get customer attachment type
export function getAllCustomerAttachmentType() {
  return customerAttachmentTypes;
}

// get customer attachment type by id
export function getCustomerAttachmentTypeById(id) {
  if (!id) {
    return "";
  }
  return customerAttachmentTypes.find((item) => item.id === id);
}

// customer attachment pdf print hook
export function useCustomerAttachmentPrint(name) {
  const [printParams, setPrintParams] = useState({
    caid: "",
    time: "",
    printDate: printDate(new Date()),
    print_pad_type: "1"
  });

  const [printCount, setPrintCount] = useState(0);

  const { isLoading: printLoading, data: printData } = usePrintCustomerAttachmentQuery({
    query: `?${objectToQueryString(printParams)}`,
    options: {
      enabled: !!printParams?.caid
    },
    key: `Print${name ?? ""}-${printCount}`
  });

  // quotation view handler
  // Convert base64 to array buffer
  const base64ToArrayBuffer = (base64) => {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const openPdfInNewTab = useCallback((data) => {
    if (data) {
      const arrayBuffer = base64ToArrayBuffer(data);
      const blob = new Blob([arrayBuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      // Open the PDF in a new tab
      window.open(url, "_blank");
    }
  }, []);

  useEffect(() => {
    openPdfInNewTab(printData?.data);
  }, [openPdfInNewTab, printData?.data, printCount]);

  return { printParams, setPrintParams, setPrintCount, printLoading, printCount };
}

/**
 * Export money receipts to CSV (Excel-compatible) via server-side streamed download.
 */
export function useExportMoneyReceiptsCsv() {
  const { axiosInstance } = useAxiosInstance();
  const [exporting, setExporting] = useState(false);

  const exportCsv = useCallback(
    async (params, filename = "money_receipts.csv") => {
      try {
        setExporting(true);
        const response = await axiosInstance.get("/customer/attachments/export-csv", {
          params,
          responseType: "blob"
        });

        const blob = new Blob([response.data], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
      } catch (error) {
        return errorFormat(error);
      } finally {
        setExporting(false);
      }
    },
    [axiosInstance]
  );

  return { exporting, exportCsv };
}
